import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, MessageSquare, BookOpen, Wrench, ShieldCheck, Ban, GitBranch,
  GraduationCap, Activity as ActIcon, SlidersHorizontal, ListChecks,
} from "lucide-react";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { StatusBadge } from "@/components/StatusBadge";
import { EmployeePanel } from "@/components/EmployeePanel";
import { useStore } from "@/lib/store";
import { AUTHORITY } from "@/data/seed";

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="glass rounded-2xl p-5" data-testid={testid}>
      <div className="mb-3 flex items-center gap-2 text-sm font-medium text-white"><Icon size={16} className="text-[#00E5FF]" /> {title}</div>
      {children}
    </div>
  );
}
const Chips = ({ items, color = "rgba(0,229,255,0.12)", text = "#7CE0FF" }) => (
  <div className="flex flex-wrap gap-2">{items.map((it) => <span key={it} className="rounded-full px-2.5 py-1 text-xs" style={{ background: color, color: text }}>{it}</span>)}</div>
);

export default function EmployeeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useStore();
  const e = state.employees.find((x) => x.id === id);
  const [talk, setTalk] = useState(false);

  if (!e) return (
    <div className="glass rounded-2xl p-10 text-center text-slate-400" data-testid="profile-empty">
      Associate not found. <button onClick={() => navigate("/employees")} className="text-[#00E5FF]">Back to directory</button>
    </div>
  );

  const auth = AUTHORITY[e.authority];
  const timeline = state.activity.filter((a) => a.employee === e.id).slice(0, 8);

  return (
    <div>
      {talk && <EmployeePanel employeeId={e.id} initialMode="talk" onClose={() => setTalk(false)} />}
      <button onClick={() => navigate("/employees")} className="mb-4 flex items-center gap-1.5 text-sm text-slate-400 hover:text-white" data-testid="profile-back"><ArrowLeft size={15} /> My Associates</button>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass-strong relative overflow-hidden rounded-2xl p-6">
        <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full" style={{ background: "radial-gradient(circle, rgba(0,229,255,0.2), transparent 70%)" }} />
        <div className="flex flex-wrap items-center gap-5">
          <EmployeeAvatar employee={e} size={92} />
          <div className="flex-1">
            <h1 className="font-display text-3xl font-semibold text-white">{e.name}</h1>
            <div className="text-slate-300">{e.title}</div>
            <div className="mt-2 flex flex-wrap items-center gap-3"><StatusBadge status={e.status} /><span className="text-xs text-slate-500">Personality: {e.personality}</span></div>
          </div>
          <button onClick={() => setTalk(true)} className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-4 py-2 text-sm font-semibold text-[#030A16]" data-testid="profile-talk"><MessageSquare size={15} /> Talk to {e.name}</button>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[["Tasks today", e.tasksToday], ["Tasks month", e.tasksMonth], ["Open", e.openAssignments], ["Hours saved", e.hoursSaved]].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-white/8 bg-[#030A16]/40 p-3"><div className="font-display text-2xl text-white">{v}</div><div className="text-[11px] text-slate-500">{l}</div></div>
          ))}
        </div>
      </motion.div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section icon={BookOpen} title="Job description & responsibilities" testid="profile-jobdesc">
          <p className="text-sm text-slate-300">{e.jobDescription}</p>
          <ul className="mt-3 space-y-1.5">{e.responsibilities.map((r) => <li key={r} className="flex gap-2 text-sm text-slate-300"><ListChecks size={15} className="mt-0.5 shrink-0 text-[#22D3EE]" /> {r}</li>)}</ul>
        </Section>

        <Section icon={SlidersHorizontal} title="Voice & personality" testid="profile-voice">
          <div className="space-y-2 text-sm text-slate-300">
            <div><span className="text-slate-500">Voice: </span>{e.voice}</div>
            <div><span className="text-slate-500">Personality: </span>{e.personality}</div>
            <div><span className="text-slate-500">Greeting: </span><span className="italic">"{e.greeting}"</span></div>
          </div>
        </Section>

        <Section icon={BookOpen} title="Knowledge sources" testid="profile-knowledge"><Chips items={e.knowledgeSources} /></Section>
        <Section icon={Wrench} title="Connected tools" testid="profile-tools"><Chips items={e.connectedTools} color="rgba(138,43,226,0.14)" text="#C4A7F5" /></Section>

        <Section icon={ShieldCheck} title="Authority level" testid="profile-authority">
          <div className="rounded-lg border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-3"><div className="text-white">{auth.label}</div><div className="text-xs text-slate-400">{auth.desc}</div></div>
        </Section>
        <Section icon={Ban} title="Restrictions" testid="profile-restrictions">
          <ul className="space-y-1.5">{e.restrictions.map((r) => <li key={r} className="flex gap-2 text-sm text-slate-300"><Ban size={14} className="mt-0.5 shrink-0 text-[#FF0055]" /> {r}</li>)}</ul>
        </Section>

        <Section icon={GitBranch} title="Escalation rules" testid="profile-escalation">
          <ul className="space-y-1.5">{e.escalationRules.map((r) => <li key={r} className="flex gap-2 text-sm text-slate-300"><GitBranch size={14} className="mt-0.5 shrink-0 text-[#FFBF00]" /> {r}</li>)}</ul>
        </Section>
        <Section icon={GraduationCap} title="Training history" testid="profile-training">
          <div className="space-y-2">{e.trainingHistory.map((t, i) => <div key={i} className="rounded-lg border border-white/8 bg-[#030A16]/40 p-2.5"><div className="font-mono text-[11px] text-[#00E5FF]">{t.date}</div><div className="text-sm text-slate-300">{t.rule}</div></div>)}</div>
        </Section>

        <Section icon={ActIcon} title="Recent work" testid="profile-recent">
          <div className="space-y-2">
            {timeline.length === 0 && <div className="text-sm text-slate-500">No recent activity.</div>}
            {timeline.map((a) => <div key={a.id} className="flex gap-3 rounded-lg border border-white/8 bg-[#030A16]/40 p-2.5"><span className="font-mono text-xs text-[#00E5FF]">{a.time}</span><div className="min-w-0"><div className="truncate text-sm text-slate-200">{a.task}</div><div className="truncate text-[11px] text-slate-500">{a.action} · {a.result}</div></div></div>)}
          </div>
        </Section>
      </div>
    </div>
  );
}
